import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";

const PAYROLL_BASE_URL = `${API_ENDPOINT.Base_URL}/api/v1/payroll`;
const POLL_INTERVAL_MS = 2500;
const MAX_STATUS_ATTEMPTS = 40;
const SUCCESS_STATUS_SET = new Set(["COMPLETED", "COMPLETED_WITH_ERRORS"]);
const FAILURE_STATUS_SET = new Set(["FAILED", "ERROR"]);

const ENDPOINTS = {
  upload: `${PAYROLL_BASE_URL}/bulk-upload`,
  status: `${PAYROLL_BASE_URL}/bulk-status`,
  results: `${PAYROLL_BASE_URL}/bulk-results`,
  save: `${PAYROLL_BASE_URL}/save-service-match`,
  overallResults: `${PAYROLL_BASE_URL}/overall-results`,
};

const getAuthToken = () => {
  try {
    const persistedMain = JSON.parse(localStorage.getItem("persist:main") || "{}");
    const loginData = persistedMain?.loginData
      ? JSON.parse(persistedMain.loginData)
      : persistedMain?.value
      ? JSON.parse(persistedMain.value)
      : null;

    return (
      loginData?.value?.auth_token ||
      loginData?.auth_token ||
      loginData?.access_token ||
      loginData?.token ||
      ""
    );
  } catch {
    return "";
  }
};

const getAuthHeaders = () => {
  const authToken = getAuthToken();
  return authToken ? { Authorization: `Bearer ${authToken}` } : {};
};

const getLoginData = () => {
  try {
    const persistedMain = JSON.parse(localStorage.getItem("persist:main") || "{}");
    return persistedMain?.loginData
      ? JSON.parse(persistedMain.loginData)
      : persistedMain?.value
      ? JSON.parse(persistedMain.value)
      : {};
  } catch {
    return {};
  }
};

const getBuyerToken = () => {
  const loginData = getLoginData();
  return (
    loginData?.value?.user?.buyer_token ||
    loginData?.value?.buyer_token ||
    loginData?.user?.buyer_token ||
    loginData?.buyer_token ||
    loginData?.value?.user?.user_token ||
    loginData?.user?.user_token ||
    ""
  );
};

const toDisplayValue = (value, fallback = "-") =>
  value === null || value === undefined || value === "" ? fallback : value;

const toNumberValue = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const sleep = (duration) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, duration);
  });

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const normalizeExchangeRate = (exchangeRate = {}) => ({
  sourceCurrency: exchangeRate.source_currency || "",
  targetCurrency: exchangeRate.target_currency || "INR",
  exchangeRate: exchangeRate.exchange_rate,
  exchangeRateDate: exchangeRate.exchange_rate_date,
});

const normalizeLineItem = (item = {}, index) => ({
  id: `${item.consultant_name || "consultant"}-${index}`,
  consultantName: toDisplayValue(item.consultant_name),
  role: toDisplayValue(item.role),
  currency: toDisplayValue(item.currency),
  hourlyRate: item.hourly_rate,
  fixedFee: item.fixed_fee,
  approvedHours: item.approved_hours,
  billingPercentage: item.billing_percentage,
  billableHours: item.billable_hours,
  expectedAmount: item.expected_amount,
  exchangeRate: item.exchange_rate,
  exchangeRateDate: item.exchange_rate_date,
  inrAmount: item.inr_amount,
  contractStatus: toDisplayValue(item.contract_status),
  approvalStatus: toDisplayValue(item.approval_status),
  periodStatus: toDisplayValue(item.period_status),
  billingStatus: toDisplayValue(item.billing_status),
  overallStatus: toDisplayValue(item.overall_status),
  remarks: toDisplayValue(item.remarks),
  matchStatus: toDisplayValue(item.match_status),
  invoiceStatus: toDisplayValue(item.invoice_status),
  invoiceHours: item.invoice_hours,
  invoiceHourlyRate: item.invoice_hourly_rate,
  invoiceAmount: item.invoice_amount,
  invoiceInrAmount: item.invoice_inr_amount,
  invoiceVariance: item.invoice_variance,
});

export const normalizeServiceMatch = (serviceMatch = {}, batchToken = "") => {
  const exchangeRate = normalizeExchangeRate(serviceMatch.exchange_rate);
  const lineItems = Array.isArray(serviceMatch.line_items)
    ? serviceMatch.line_items.map(normalizeLineItem)
    : [];

  return {
    id: serviceMatch.service_match_id,
    batchToken,
    serviceMatchId: toDisplayValue(serviceMatch.service_match_id),
    sowNo: toDisplayValue(serviceMatch.sow_no),
    contractRef: toDisplayValue(serviceMatch.contract_ref),
    vendor: toDisplayValue(serviceMatch.vendor),
    servicePeriod: toDisplayValue(serviceMatch.service_period),
    timesheetRef: toDisplayValue(serviceMatch.timesheet_ref),
    currency: toDisplayValue(serviceMatch.currency),
    createdDate: toDisplayValue(serviceMatch.created_date),
    overallStatus: toDisplayValue(serviceMatch.overall_status),
    matchingMode: toDisplayValue(serviceMatch.matching_mode),
    exchangeRate,
    summary: {
      overallStatus: toDisplayValue(serviceMatch.summary?.overall_status),
      matchedCount: serviceMatch.summary?.matched_count,
      unmatchedCount: serviceMatch.summary?.unmatched_count,
      contractCoverage: toDisplayValue(serviceMatch.summary?.contract_coverage),
      timesheetApproval: toDisplayValue(serviceMatch.summary?.timesheet_approval),
      specialBillingConditions: toDisplayValue(
        serviceMatch.summary?.special_billing_conditions,
      ),
      periodIssues: toDisplayValue(serviceMatch.summary?.period_issues),
      originalExpectedBilling: serviceMatch.summary?.original_expected_billing,
      expectedBillingInInr: serviceMatch.summary?.expected_billing_in_inr,
      invoiceAmount: serviceMatch.summary?.invoice_amount,
      invoiceInrAmount: serviceMatch.summary?.invoice_inr_amount,
      invoiceVariance: serviceMatch.summary?.invoice_variance,
    },
    totals: {
      resources: serviceMatch.totals?.resources,
      approvedHours: serviceMatch.totals?.approved_hours,
      billableHours: serviceMatch.totals?.billable_hours,
      expectedAmount: serviceMatch.totals?.expected_amount,
      inrAmount: serviceMatch.totals?.inr_amount,
      invoiceAmount: serviceMatch.totals?.invoice_amount,
      invoiceInrAmount: serviceMatch.totals?.invoice_inr_amount,
      varianceAmount: serviceMatch.totals?.variance_amount,
    },
    sourceDocuments: {
      sowFileName: serviceMatch.source_documents?.sow_file_name,
      timesheetFileName: serviceMatch.source_documents?.timesheet_file_name,
      invoiceFileName: serviceMatch.source_documents?.invoice_file_name,
    },
    invoice: {
      invoiceNumber: serviceMatch.invoice?.invoice_number,
      invoiceDate: serviceMatch.invoice?.invoice_date,
      vendor: serviceMatch.invoice?.vendor,
      currency: serviceMatch.invoice?.currency,
      invoiceTotal: serviceMatch.invoice?.invoice_total,
      invoiceInrAmount: serviceMatch.invoice?.invoice_inr_amount,
    },
    matchedItems: Array.isArray(serviceMatch.matched_items) ? serviceMatch.matched_items : [],
    unmatchedItems: Array.isArray(serviceMatch.unmatched_items) ? serviceMatch.unmatched_items : [],
    lineItems,
  };
};

export const toServiceMatchingListRecord = (details = {}) => ({
  id: details.id || details.serviceMatchId,
  serviceMatchId: details.serviceMatchId,
  sowNo: details.sowNo,
  vendor: details.vendor,
  servicePeriod: details.servicePeriod,
  timesheetRef: details.timesheetRef,
  matchingMode: details.matchingMode,
  invoiceNumber: details.invoice?.invoiceNumber,
  resources: details.totals?.resources,
  approvedHours: details.totals?.approvedHours,
  expectedBilling: details.totals?.expectedAmount,
  invoiceAmount: details.totals?.invoiceAmount,
  invoiceInrAmount: details.totals?.invoiceInrAmount,
  varianceAmount: details.totals?.varianceAmount,
  currency: details.currency,
  inrEquivalent: details.totals?.inrAmount,
  overallStatus: details.overallStatus,
  createdDate: details.createdDate,
  exchangeRate: details.exchangeRate,
});

const getServiceMatchFromRecord = (record = {}) =>
  record.service_match || record.serviceMatch || record.matching_result || record.result || record;

const extractOverallRecords = (payload = {}) => {
  const payloadData = payload.data && !Array.isArray(payload.data) ? payload.data : {};
  const twoWayMatches = Array.isArray(payload.two_way_matches)
    ? payload.two_way_matches
    : payloadData.two_way_matches || [];
  const threeWayMatches = Array.isArray(payload.three_way_matches)
    ? payload.three_way_matches
    : payloadData.three_way_matches || [];

  if (twoWayMatches.length || threeWayMatches.length) {
    return [
      ...twoWayMatches.map((record) => ({ ...record, matching_mode: record.matching_mode || "2-way" })),
      ...threeWayMatches.map((record) => ({ ...record, matching_mode: record.matching_mode || "3-way" })),
    ];
  }

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data?.results)) return payload.data.results;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.service_matches)) return payload.service_matches;
  if (Array.isArray(payloadData.service_matches)) return payloadData.service_matches;
  if (Array.isArray(payload.records)) return payload.records;
  if (Array.isArray(payloadData.records)) return payloadData.records;
  if (Array.isArray(payload.overall_results)) return payload.overall_results;
  if (Array.isArray(payloadData.overall_results)) return payloadData.overall_results;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payloadData.items)) return payloadData.items;
  return [];
};

const normalizeOverallRecord = (record = {}) => {
  const batchToken = record.batch_token || record.batchToken || record.token || "";
  const serviceMatch = normalizeServiceMatch(getServiceMatchFromRecord(record), batchToken);
  return {
    ...toServiceMatchingListRecord(serviceMatch),
    details: serviceMatch,
  };
};

const getBulkResultServiceMatch = (payload = {}) => {
  const payloadData = payload.data && !Array.isArray(payload.data) ? payload.data : {};
  const threeWayMatches = Array.isArray(payload.three_way_matches)
    ? payload.three_way_matches
    : payloadData.three_way_matches || [];
  const twoWayMatches = Array.isArray(payload.two_way_matches)
    ? payload.two_way_matches
    : payloadData.two_way_matches || [];

  return (
    payload.service_match ||
    payload.serviceMatch ||
    payload.matching_result ||
    payload.result ||
    payloadData.service_match ||
    payloadData.serviceMatch ||
    payloadData.matching_result ||
    payloadData.result ||
    threeWayMatches[0] ||
    twoWayMatches[0] ||
    extractOverallRecords(payload)[0]
  );
};

const normalizeBulkResultsResponse = (payload = {}, batchToken = "") => {
  const serviceMatch = getBulkResultServiceMatch(payload) || {};
  return {
    ...payload,
    serviceMatch: normalizeServiceMatch(serviceMatch, payload.batch_token || batchToken),
  };
};

export const getServiceMatchingList = async ({
  search = "",
  page = 1,
  sortKey = "createdDate",
  sortOrder = "desc",
} = {}) => {
  const sortKeyMap = {
    createdDate: "created_date",
    expectedBilling: "expected_amount",
    invoiceAmount: "invoice_amount",
    invoiceInrAmount: "invoice_inr_amount",
    varianceAmount: "variance_amount",
    inrEquivalent: "inr_amount",
  };

  try {
    const { data } = await axios.post(ENDPOINTS.overallResults, {
      buyer_token: getBuyerToken(),
      search,
      page,
      sort_key: sortKeyMap[sortKey] || sortKey,
      sort_order: sortOrder,
    }, {
      headers: getAuthHeaders(),
    });
    const paginationSource =
      data?.data && !Array.isArray(data.data) ? data.data : data;
    const records = extractOverallRecords(data);

    return {
      records: records.map(normalizeOverallRecord),
      pagination: {
        currentPage: paginationSource.current_page || paginationSource.page || page,
        pageCount:
          paginationSource.page_count ||
          paginationSource.total_pages ||
          paginationSource.pages ||
          1,
        totalCount:
          paginationSource.total_count ||
          paginationSource.count ||
          paginationSource.total ||
          records.length,
      },
    };
  } catch (error) {
    throw new Error(getErrorMessage(error, "Unable to fetch service payment results."));
  }
};

export const getServiceMatchingDetails = (recordOrId) =>
  Promise.resolve(
    typeof recordOrId === "object" && recordOrId?.details
      ? recordOrId.details
      : null,
  );

export const createServiceMatching = async ({ files = [] } = {}) => {
  const loginData = getLoginData();
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });
  formData.append(
    "org_token",
    loginData?.value?.organization_token ||
      loginData?.value?.org_token ||
      loginData?.value?.user?.organization_token ||
      loginData?.organization_token ||
      loginData?.org_token ||
      loginData?.user?.organization_token ||
      getBuyerToken() ||
      "",
  );

  try {
    const { data } = await axios.post(ENDPOINTS.upload, formData, {
      headers: getAuthHeaders(),
    });

    if (!data?.success || !data?.batch_token) {
      throw new Error("Upload failed. Batch token was not returned.");
    }

    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Unable to upload service documents."));
  }
};

export const getServiceMatchingBulkStatus = async (batchToken) => {
  try {
    const { data } = await axios.post(ENDPOINTS.status, {
      batch_token: batchToken,
    }, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Unable to check processing status."));
  }
};

export const waitForServiceMatchingCompletion = async (batchToken, onProgress) => {
  for (let attempt = 1; attempt <= MAX_STATUS_ATTEMPTS; attempt += 1) {
    const status = await getServiceMatchingBulkStatus(batchToken);
    const normalizedStatus = String(status?.status || "").toUpperCase();
    if (typeof onProgress === "function") onProgress(status);

    if (SUCCESS_STATUS_SET.has(normalizedStatus)) return status;
    if (FAILURE_STATUS_SET.has(normalizedStatus)) {
      throw new Error(status?.error || "Service payment processing failed.");
    }

    await sleep(POLL_INTERVAL_MS);
  }

  throw new Error("Service payment processing timed out. Please try again.");
};

export const getServiceMatchingBulkResults = async (batchToken) => {
  try {
    const { data } = await axios.post(ENDPOINTS.results, {
      batch_token: batchToken,
    }, {
      headers: getAuthHeaders(),
    });
    return normalizeBulkResultsResponse(data, batchToken);
  } catch (error) {
    if (error?.response?.data) {
      return normalizeBulkResultsResponse(error.response.data, batchToken);
    }

    throw new Error(getErrorMessage(error, "Unable to fetch matching result."));
  }
};

export const saveServiceMatchingResult = async (batchToken) => {
  if (!batchToken) {
    throw new Error("Batch token is missing. Please compare documents again.");
  }

  try {
    const { data } = await axios.post(ENDPOINTS.save, {
      batch_token: batchToken,
    }, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Unable to save service payment result."));
  }
};

export const compareServiceDocuments = async ({ files = [], onProgress } = {}) => {
  if (typeof onProgress === "function") {
    onProgress({ stage: "uploading", status: "UPLOADING" });
  }
  const upload = await createServiceMatching({ files });
  if (typeof onProgress === "function") {
    onProgress({ stage: "processing", ...upload });
  }
  await waitForServiceMatchingCompletion(upload.batch_token, onProgress);
  if (typeof onProgress === "function") {
    onProgress({ stage: "fetching", status: "FETCHING_RESULTS" });
  }
  const result = await getServiceMatchingBulkResults(upload.batch_token);
  if (typeof onProgress === "function") {
    onProgress({ stage: "completed", status: result.status });
  }

  return result.serviceMatch;
};

export const getSortableAmount = toNumberValue;
